/**
 * Attendance Edit Page — hardcoded form.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const STATUS_OPTIONS = ['present', 'absent', 'half_day', 'late', 'on_leave', 'holiday', 'work_from_home'];

function toTimeStr(ts: unknown): string {
  if (!ts) return '';
  const s = String(ts);
  if (s.includes('T')) return s.slice(11, 16);
  return s.slice(0, 5);
}

export function AttendanceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'attendance_logs', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => {
    if (data?.data) {
      const d = data.data as Record<string, unknown>;
      setForm({
        ...d,
        _check_in_time: toTimeStr(d.check_in_time),
        _check_out_time: toTimeStr(d.check_out_time),
      });
    }
  }, [data]);

  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, created_at, updated_at, total_hours, _check_in_time, _check_out_time, ...values } = form;
    const dateStr = String(values.date || '').slice(0, 10);
    const payload = {
      ...values,
      check_in_time: _check_in_time ? `${dateStr}T${_check_in_time}:00` : null,
      check_out_time: _check_out_time ? `${dateStr}T${_check_out_time}:00` : null,
    };
    update({ resource: 'attendance_logs', id: id!, values: payload }, { onSuccess: () => navigate(`/attendance/${id}`) });
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/attendance/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Edit Attendance</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="date">Date</Label>
                <Input id="date" type="date" value={String(form.date ?? '').slice(0, 10)} onChange={e => set('date', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={String(form.status ?? 'present')}
                  onChange={e => set('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="check_in_time">Check-In Time</Label>
                <Input id="check_in_time" type="time" value={String(form._check_in_time ?? '')} onChange={e => set('_check_in_time', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="check_out_time">Check-Out Time</Label>
                <Input id="check_out_time" type="time" value={String(form._check_out_time ?? '')} onChange={e => set('_check_out_time', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea id="remarks" value={String(form.remarks ?? '')} onChange={e => set('remarks', e.target.value)} rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/attendance/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AttendanceEditPage;
